import IntegrationCommunication from 'services/integration-communication';
import type { Logger } from 'services/Logger';

import { inIframe } from './common';
import { PAGE_STATUS } from './constants';

interface redirectProps {
  urlToRedirect?: string;
  merchantRedirectType?: string;
  isOpenedInPopup: boolean;
  logger?: Logger;
  isInWeb?: () => boolean;
}

interface RedirectUrlProps {
  successRedirectUrl?: string;
  processingRedirectUrl?: string;
  failRedirectUrl?: string;
  merchantRedirectUrl?: string;
  status: PAGE_STATUS;
}

export const fullyDecodeURI = (url: string): string => {
  const MAX_ITERATIONS = 10;
  let result = url;
  for (let i = 0; i < MAX_ITERATIONS; i += 1) {
    try {
      const decoded = decodeURIComponent(result);
      if (decoded === result) break;
      result = decoded;
    } catch {
      break;
    }
  }
  return result;
};

export const isDeeplink = (rawUrl: string): boolean => {
  if (!rawUrl) return false;

  const url = fullyDecodeURI(rawUrl.trim());

  const match = url.match(/^([a-zA-Z][a-zA-Z\d+\-.]*):/);

  if (!match) {
    return false;
  }

  const scheme = match[1].toLowerCase();

  return scheme !== 'http' && scheme !== 'https';
};

export const processUrl = (rawUrl: string) => {
  if (!rawUrl) return rawUrl;
  const url = fullyDecodeURI(rawUrl.trim());

  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(url)) {
    return url;
  }
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url)) {
    return url;
  }
  return `https://${url}`;
};

export const handleRedirectInFrame = (urlToRedirect: string, isOpenedInPopup: boolean) => {
  if (urlToRedirect) {
    IntegrationCommunication.sendMessage({
      id: 'redirectTo',
      value: urlToRedirect,
    });
    return;
  }
  if (isOpenedInPopup) {
    IntegrationCommunication.sendMessage({
      id: 'closeCashier',
    });
    return;
  }

  IntegrationCommunication.sendMessage({
    id: 'reInitCashier',
  });
};

export const handleRedirect = (props: redirectProps): void => {
  const { urlToRedirect = '', merchantRedirectType = '', isOpenedInPopup, logger, isInWeb: isInWebCallback } = props;
  const isInWeb = isInWebCallback ? isInWebCallback() : inIframe();
  logger?.info(
    `handleRedirect before redirect urlToRedirect, merchantRedirectType: 
        ${urlToRedirect}, 
        ${merchantRedirectType}`,
  );
  if (!urlToRedirect) {
    return;
  }

  if (isInWeb) {
    handleRedirectInFrame(urlToRedirect, isOpenedInPopup);
    return;
  }
  const isBlankRedirect = merchantRedirectType?.toLowerCase() === 'blank';
  if (isBlankRedirect) {
    window.open(urlToRedirect, '_blank', 'noopener,noreferrer');
    return;
  }

  window.location.assign(urlToRedirect);
};

export const appendCloseButtonActionParam = (url: string, depositCompleted = false): string => {
  if (!url || !isDeeplink(url)) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}action=close_button&deposit_completed=${depositCompleted}`;
};

export const getRedirectUrlByStatus = (props: RedirectUrlProps) => {
  const { successRedirectUrl, processingRedirectUrl, failRedirectUrl, merchantRedirectUrl = '', status } = props;
  const params = new URLSearchParams(window.location.search);
  const merchantRedirectQueryUrl = processUrl(params.get('merchant_redirect_url') || '');
  const merchantDomainUrl = processUrl(params.get('merchant_domain') || '');
  const baseRedirectUrl = merchantRedirectQueryUrl || merchantDomainUrl || merchantRedirectUrl;

  switch (status) {
    case PAGE_STATUS.success: {
      return successRedirectUrl || baseRedirectUrl;
    }

    case PAGE_STATUS.processing: {
      return processingRedirectUrl || baseRedirectUrl;
    }

    case PAGE_STATUS.error: {
      return failRedirectUrl || baseRedirectUrl;
    }

    default: {
      return baseRedirectUrl;
    }
  }
};
